import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Player from '@kinescope/react-kinescope-player'
import { useTelegramWebApp } from '../../../hooks/useTelegramWebApp'
import { ENV } from '../../../shared/config/env'
import { logger } from '../../../services/logger'
import './KinescopePlayer.css'

interface KinescopePlayerProps {
  src: string
  height: number
  caption?: string
  blockId: string
}

// Платформа Telegram-клиента (или 'web' для web-сборки) для контекста логов.
// Берём напрямую из window.Telegram, т.к. useTelegramWebApp в дочерних
// компонентах возвращает null (см. развилку KinescopePlayer ниже).
const getPlayerPlatform = (): string => {
  if (ENV.IS_WEB) return 'web'
  if (typeof window === 'undefined') return 'unknown'
  return window.Telegram?.WebApp?.platform || 'unknown'
}

// videoId из src — для диагностики какое именно видео не грузится
const extractVideoId = (src: string): string => {
  try {
    return new URL(src).pathname.split('/').filter(Boolean).pop() || ''
  } catch {
    return ''
  }
}

// Единый формат диагностического лога ошибок плеера
const logPlayerEvent = (
  errorType: string,
  src: string,
  blockId: string,
  error?: unknown
) => {
  const payload =
    error instanceof Error
      ? { name: error.name, message: error.message, stack: error.stack }
      : error !== undefined
        ? { detail: error }
        : { detail: errorType }

  void logger.logError(payload, {
    errorType,
    additionalData: {
      video_id: extractVideoId(src),
      src,
      block_id: blockId,
      player_platform: getPlayerPlatform(),
    },
  })
}

// Сколько ждём загрузки iframe (onLoad) в TG mobile, прежде чем счесть, что видео не загрузилось
const IFRAME_LOAD_TIMEOUT_MS = 15000

// Диагностика загрузки сырого <iframe> (путь TG mobile): нативный onError +
// таймаут, если onLoad не сработал за IFRAME_LOAD_TIMEOUT_MS. Возвращает
// пропсы onLoad/onError для навешивания на <iframe>.
const useIframeLoadDiagnostics = (src: string, blockId: string) => {
  const loadedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    loadedRef.current = false
    timerRef.current = setTimeout(() => {
      if (!loadedRef.current) {
        logPlayerEvent('kinescope_iframe_load_timeout', src, blockId, {
          timeout_ms: IFRAME_LOAD_TIMEOUT_MS,
        })
      }
    }, IFRAME_LOAD_TIMEOUT_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [src, blockId])

  const onLoad = () => {
    loadedRef.current = true
    if (timerRef.current) clearTimeout(timerRef.current)
  }

  const onError = () => {
    logPlayerEvent('kinescope_iframe_error', src, blockId)
  }

  return { onLoad, onError }
}

const KinescopePlayerWeb: React.FC<KinescopePlayerProps> = ({
  src,
  height,
  caption,
  blockId,
}) => {
  let videoId = ''
  let watermark: string | undefined
  try {
    const url = new URL(src)
    videoId = url.pathname.split('/').filter(Boolean).pop() || ''
    watermark = url.searchParams.get('watermark') || undefined
  } catch {
    videoId = ''
  }

  return (
    <div className="kinescope-player kinescope-player--web">
      <div className="player-container">
        <Player
          videoId={videoId}
          width="100%"
          height={height}
          watermark={watermark}
          title={`Видео ${blockId}`}
          onJSLoadError={() =>
            logPlayerEvent('kinescope_js_load_error', src, blockId)
          }
          onInitError={error =>
            logPlayerEvent('kinescope_init_error', src, blockId, error)
          }
          onError={data =>
            logPlayerEvent('kinescope_playback_error', src, blockId, data)
          }
        />
      </div>
      {caption && <p className="embed-caption">{caption}</p>}
    </div>
  )
}

const KinescopePlayerTelegram: React.FC<KinescopePlayerProps> = ({
  src,
  height,
  caption,
  blockId,
}) => {
  const { platform, webApp } = useTelegramWebApp()
  // Показываем кнопку полноэкранного режима только для Android
  const showFullscreenButton = platform === 'android'
  const [isFullscreen, setIsFullscreen] = useState(false)

  const playerRef = useRef<HTMLDivElement>(null)

  // Создаем функцию для блокировки touch событий, чтобы можно было правильно их удалить
  const preventScrollRef = useRef<((e: TouchEvent) => void) | null>(null)

  // Сохраняем позицию скролла для восстановления
  const scrollPositionRef = useRef<number>(0)

  // Для сохранения состояния видео между обычным и полноэкранным режимом
  const [currentVideoSrc, setCurrentVideoSrc] = useState(src)

  // Ref'ы для iframe'ов чтобы получать доступ к видео
  const normalIframeRef = useRef<HTMLIFrameElement>(null)
  const fullscreenIframeRef = useRef<HTMLIFrameElement>(null)

  // Сохраняем текущее время воспроизведения
  const videoTimeRef = useRef<number>(0)
  const isVideoPlayingRef = useRef<boolean>(false)

  // Диагностика загрузки iframe (TG mobile): onError + таймаут onLoad
  const iframeDiagnostics = useIframeLoadDiagnostics(src, blockId)

  // Функция для добавления автоплея и времени начала к URL
  const addVideoStateToUrl = (
    baseUrl: string,
    currentTime?: number,
    autoplay?: boolean
  ) => {
    try {
      const url = new URL(baseUrl)

      if (currentTime && currentTime > 0) {
        url.searchParams.set('t', Math.floor(currentTime).toString())
      }

      if (autoplay !== undefined) {
        url.searchParams.set('autoplay', autoplay ? '1' : '0')
      }

      return url.toString()
    } catch (error) {
      console.warn('Ошибка при обработке URL видео:', error)
      return baseUrl
    }
  }

  // Функция для попытки получения текущего времени через postMessage
  const tryGetVideoTime = (iframe: HTMLIFrameElement | null) => {
    if (!iframe?.contentWindow) return

    try {
      // Отправляем запрос на получение текущего времени
      iframe.contentWindow.postMessage({ action: 'getCurrentTime' }, '*')
    } catch (error) {
      console.warn('Не удалось получить время видео:', error)
    }
  }

  const handleFullscreen = () => {
    if (!isFullscreen) {
      // Попытаемся получить текущее время видео перед переключением
      tryGetVideoTime(normalIframeRef.current)

      // Даем небольшую задержку для получения ответа от плеера
      setTimeout(() => {
        // Сохраняем текущую позицию скролла
        scrollPositionRef.current =
          window.pageYOffset || document.documentElement.scrollTop

        // Обновляем URL видео с текущим временем и автоплеем
        const newVideoSrc = addVideoStateToUrl(src, videoTimeRef.current, true)
        setCurrentVideoSrc(newVideoSrc)

        // Переходим в полноэкранный режим
        setIsFullscreen(true)

        // Блокируем скролл страницы и touch события
        document.body.style.overflow = 'hidden'
        document.body.style.position = 'fixed'
        document.body.style.top = `-${scrollPositionRef.current}px`
        document.body.style.width = '100%'
        document.body.style.height = '100%'

        // Дополнительные меры для мобильных устройств
        document.documentElement.style.overflow = 'hidden'

        // Блокируем touch события на документе (только scroll, не все touch события)
        preventScrollRef.current = (e: TouchEvent) => {
          // Проверяем, что это не клик по кнопке закрытия
          const target = e.target as Element
          if (
            target?.closest('.fullscreen-btn') ||
            target?.closest('.kinescope-player.fullscreen')
          ) {
            return // Разрешаем touch события на видео и кнопке
          }
          e.preventDefault()
          e.stopPropagation()
        }
        document.addEventListener('touchmove', preventScrollRef.current, {
          passive: false,
        })
      }, 100)
    } else {
      // Попытаемся получить текущее время видео перед закрытием
      tryGetVideoTime(fullscreenIframeRef.current)

      // Даем небольшую задержку для получения ответа от плеера
      setTimeout(() => {
        // Обновляем URL видео с текущим временем (без автоплея для обычного режима)
        const newVideoSrc = addVideoStateToUrl(src, videoTimeRef.current, false)
        setCurrentVideoSrc(newVideoSrc)

        // Выходим из полноэкранного режима
        setIsFullscreen(false)
      }, 100)

      // Восстанавливаем скролл
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.height = ''
      document.documentElement.style.overflow = ''

      // Восстанавливаем позицию скролла с небольшой задержкой для плавности
      setTimeout(() => {
        window.scrollTo(0, scrollPositionRef.current)
      }, 0)

      // Разблокируем touch события
      if (preventScrollRef.current) {
        document.removeEventListener('touchmove', preventScrollRef.current)
        preventScrollRef.current = null
      }
    }
  }

  const handleEscapeKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isFullscreen) {
      handleFullscreen()
    }
  }

  // Обработка клавиши Escape через document
  useEffect(() => {
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        handleFullscreen()
      }
    }

    if (isFullscreen) {
      document.addEventListener('keydown', handleDocumentKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown)
    }
  }, [isFullscreen])

  // Обработка кнопки "Назад" Telegram для закрытия полноэкранного режима
  useEffect(() => {
    if (!webApp?.BackButton || !isFullscreen) return

    const handleBackButton = () => {
      if (isFullscreen) {
        handleFullscreen() // Закрываем полноэкранный режим
      }
    }

    // Добавляем обработчик на кнопку "Назад"
    webApp.BackButton.onClick(handleBackButton)

    return () => {
      // Удаляем обработчик при размонтировании или изменении состояния
      if (webApp.BackButton.offClick) {
        webApp.BackButton.offClick(handleBackButton)
      }
    }
  }, [isFullscreen, webApp])

  // Слушаем сообщения от Kinescope плеера для получения времени воспроизведения
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Проверяем, что сообщение от Kinescope
      if (
        event.origin.includes('kinescope.io') ||
        event.origin.includes('kinescope.ru')
      ) {
        try {
          const data =
            typeof event.data === 'string' ? JSON.parse(event.data) : event.data

          if (
            data.type === 'currentTime' &&
            typeof data.currentTime === 'number'
          ) {
            videoTimeRef.current = data.currentTime
          }

          if (
            data.type === 'timeupdate' &&
            typeof data.currentTime === 'number'
          ) {
            videoTimeRef.current = data.currentTime
          }

          if (data.type === 'play') {
            isVideoPlayingRef.current = true
          }

          if (data.type === 'pause') {
            isVideoPlayingRef.current = false
          }
        } catch (error) {
          // Игнорируем ошибки парсинга
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  // Очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      if (isFullscreen) {
        document.body.style.overflow = ''
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.height = ''
        document.documentElement.style.overflow = ''

        // Восстанавливаем позицию скролла
        setTimeout(() => {
          window.scrollTo(0, scrollPositionRef.current)
        }, 0)

        if (preventScrollRef.current) {
          document.removeEventListener('touchmove', preventScrollRef.current)
          preventScrollRef.current = null
        }
      }
    }
  }, [isFullscreen])

  // Рендер полноэкранного режима через портал
  const renderFullscreenPlayer = () => {
    if (!isFullscreen) return null

    return createPortal(
      <>
        {/* Overlay для блокировки взаимодействия со страницей */}
        <div
          className="fullscreen-overlay"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            handleFullscreen()
          }}
        />

        {/* Полноэкранный плеер */}
        <div
          ref={playerRef}
          className="kinescope-player fullscreen"
          onKeyDown={handleEscapeKey}
          tabIndex={-1}
        >
          <div className="player-container">
            <iframe
              ref={fullscreenIframeRef}
              src={currentVideoSrc}
              width="100%"
              height="100vh"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin"
              title={`Видео ${blockId}`}
              onLoad={iframeDiagnostics.onLoad}
              onError={iframeDiagnostics.onError}
            />

            {/* Кнопка закрытия полноэкранного режима */}
            <button
              className="fullscreen-btn"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                handleFullscreen()
              }}
              aria-label="Exit fullscreen"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      </>,
      document.body
    )
  }

  // Показываем кастомный плеер с кнопкой полноэкранного режима (для тестирования - на всех платформах)
  if (showFullscreenButton) {
    return (
      <>
        {/* Обычный плеер */}
        <div className="kinescope-player">
          <div className="player-container">
            <iframe
              ref={normalIframeRef}
              src={currentVideoSrc}
              width="100%"
              height={height}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin"
              title={`Видео ${blockId}`}
              onLoad={iframeDiagnostics.onLoad}
              onError={iframeDiagnostics.onError}
            />

            {/* Кнопка полноэкранного режима */}
            <button
              className="fullscreen-btn"
              onClick={handleFullscreen}
              aria-label="Fullscreen"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>

          {caption && <p className="embed-caption">{caption}</p>}
        </div>

        {/* Полноэкранный плеер через портал */}
        {renderFullscreenPlayer()}
      </>
    )
  }

  // Для других платформ показываем обычный iframe
  return (
    <div className="kinescope-player">
      <div className="embed-wrapper">
        <iframe
          src={currentVideoSrc}
          width="100%"
          height={height}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin"
          title={`Видео ${blockId}`}
          onLoad={iframeDiagnostics.onLoad}
          onError={iframeDiagnostics.onError}
        />
      </div>
      {caption && <p className="embed-caption">{caption}</p>}
    </div>
  )
}

const KinescopePlayer: React.FC<KinescopePlayerProps> = props => {
  // Развилка через window.Telegram.WebApp напрямую, а не через useTelegramWebApp:
  // хук имеет локальный useState и в дочерних компонентах после bootstrap не
  // переинициализирует webApp (см. ранний return в его useEffect), из-за чего
  // webApp там остаётся null. window.Telegram.WebApp заполняется TG-клиентом
  // до старта React и доступен синхронно.
  const tgPlatform =
    !ENV.IS_WEB && typeof window !== 'undefined'
      ? window.Telegram?.WebApp?.platform
      : undefined
  const isTelegramMobile = tgPlatform === 'ios' || tgPlatform === 'android'

  return isTelegramMobile ? (
    <KinescopePlayerTelegram {...props} />
  ) : (
    <KinescopePlayerWeb {...props} />
  )
}

export default KinescopePlayer
